import { Button } from '@platform/components';
import { MovingWorldsBannerLogo } from '../../layout/MovingWorldsLogo';
import { ROUTES } from '../../routes/consts';

type HeroSectionProps = {
  corporateClientId: number;
  corporateClientName: string;
  corporateClientLogo?: string | null;
};

/** These will be replaced in fast follow up with db values per discussion with program team
 * manually using this to get the Philips page out by deadline
 */

const CORPORATE_HOME_PROGRAM_NAMES: Record<number, string> = {
  1: 'Acceleration Collective',
  2: 'EY Coaching Impact x MW',
  3: 'Lumen Cares',
  4: 'F5 Volunteer Sprint',
  5: 'Accelerate Hub',
  6: 'Philips Foundation Skilled Volunteering',
};

export const HeroSection = ({
  corporateClientId,
  corporateClientName,
  corporateClientLogo,
}: HeroSectionProps) => {
  const corporateClientProgramName = CORPORATE_HOME_PROGRAM_NAMES[corporateClientId];
  return (
    <section className='flex flex-col items-center px-4 py-8 sm:px-6 md:px-8 md:py-10'>
      <div className='flex flex-col items-center gap-4 md:gap-6'>
        <div className='flex flex-col items-center gap-3 md:gap-4'>
          <HeroLogo
            corporateClientLogo={corporateClientLogo}
            corporateClientName={corporateClientName}
          />
          <h1 className='max-w-[780px] text-center text-4xl font-bold leading-[1.125] tracking-[-0.02em] text-gray-600 sm:text-5xl md:text-6xl xl:text-7xl'>
            {corporateClientProgramName}
          </h1>
          <p className='text-center text-base font-bold uppercase leading-6 tracking-[0.01em] text-gray-400 md:text-xl'>
            Powered by Movingworlds
          </p>
        </div>
        <span className='md:hidden'>
          <Button variant='primary' size='lg' href={`/platform${ROUTES.CORPORATE_DASHBOARD}`}>
            Open program dashboard
          </Button>
        </span>
        <span className='hidden md:inline-flex'>
          <Button variant='primary' size='xl' href={`/platform${ROUTES.CORPORATE_DASHBOARD}`}>
            Open program dashboard
          </Button>
        </span>
      </div>
    </section>
  );
};

const HeroLogo = ({
  corporateClientLogo,
  corporateClientName,
}: {
  corporateClientLogo?: string | null;
  corporateClientName: string;
}) => {
  const logoSize = LOGO_SIZE_MAPPING[corporateClientName] || {
    baseHeight: 'h-8',
    mdHeight: 'md:h-[112px]',
  };
  return (
    <div className='flex items-center justify-center gap-3 md:h-[43px] md:gap-5'>
      {/* Constrain by height (not width) so square and wide wordmark logos both
        sit on the same baseline as the MovingWorlds logo and the divider. */}
      {corporateClientLogo && (
        <img
          src={corporateClientLogo}
          alt={`${corporateClientName} logo`}
          className={`${logoSize.baseHeight} w-auto max-w-[120px] ${logoSize.mdHeight} md:max-w-[160px] object-contain object-center `}
        />
      )}
      <div className='hidden h-[43px] w-px bg-gray-200 md:block' />
      <div className='w-[160px] text-brand-500 md:w-[224px]'>
        <MovingWorldsBannerLogo />
      </div>
    </div>
  );
};

/** We don't have a consistent logo sizing in db logos, so we handle each individually here to account for provided logo whitespace, SVG format, etc */
const LOGO_SIZE_MAPPING: Record<string, { baseHeight: string; mdHeight: string }> = {
  SAP: {
    baseHeight: 'h-16',
    mdHeight: 'md:h-[122px]',
  },
  F5: {
    baseHeight: 'h-8',
    mdHeight: 'md:h-[64px]',
  },
  Lumen: {
    baseHeight: 'h-8',
    mdHeight: 'md:h-[92px]',
  },
  'Ernst & Young': {
    baseHeight: 'h-8',
    mdHeight: 'md:h-[76px]',
  },
  Reckitt: {
    baseHeight: 'h-12',
    mdHeight: 'md:h-[102px]',
  },
  'Philips Foundation': {
    baseHeight: 'h-8',
    mdHeight: 'md:h-[92px]',
  },
};
